// src/hooks/useAdminEndpoint.ts
import { useDispatch, useSelector } from 'react-redux'
import { RootState, AppDispatch } from 'src/redux/index'
import { resetEndpoint } from 'src/redux/IAM/AdminEndpoints/adminEndpointsSlice'
import {
  deleteEndpoint,
  getEndpoints,
  createEndpoint,
  updateEndpoint,
} from 'src/services/IAM/AdminEndpoints/AdminEndpoints.service'
import { useAlert } from 'src/hooks/useAlert'
import { usePagination } from '../Components/Pagination/usePagination'
import {
  IBodyEndpoint,
  IEndpoint,
} from 'src/services/IAM/AdminEndpoints/AdminEndpoints.interface'
import useLoading from 'src/hooks/useLoading'

/**
 * Hook `useAdminEndpoint` for managing admin endpoints. 
 * Provides CRUD functionality for managing endpoints in the app, including loading, creating, updating, and deleting.
 * Utilizes Redux for state management and supports loading indicators and alerts.
 *
 * @returns An object containing:
 * - `endpoints`: List of all available endpoints from the Redux store.
 * - `endpoint`: The currently selected endpoint.
 * - `loading`: Boolean indicating whether the process is currently loading.
 * - `error`: Any errors encountered during endpoint operations.
 * - `updateEndpointAdmin`: Function to update an existing endpoint.
 * - `createEndpointAdmin`: Function to create a new endpoint.
 * - `loadEndpoints`: Function to load the list of endpoints.
 * - `removeEndpoint`: Function to delete an endpoint by its ID.
 * - `clearEndpoints`: Function to clear the current list of endpoints in the Redux state.
 *
 * @example
 * // Example usage in a React component
 * const {
 *   endpoints,
 *   loadEndpoints,
 *   createEndpointAdmin,
 *   updateEndpointAdmin,
 *   removeEndpoint,
 *   clearEndpoints,
 * } = useAdminEndpoint();
 *
 * useEffect(() => {
 *   loadEndpoints();
 * }, []);
 */
export const useAdminEndpoint = () => {
  const { handleTotalItems } = usePagination()
  const { showAlert } = useAlert()
  const dispatch = useDispatch<AppDispatch>()
  const { startLoading, stopLoading } = useLoading()
  const { endpoints, endpoint, loading, error } = useSelector(
    (state: RootState) => state.endpoints,
  )

  /**
   * Loads a paginated list of endpoints.
   * Shows an alert based on success or failure.
   *
   * @param {Object} params - Pagination parameters.
   * @param {number} [params.currentPage=1] - The current page to load.
   * @param {number} [params.pageSize=25] - The number of items per page.
   */
  const loadEndpoints = ({
    currentPage = 1,
    pageSize = 25,
  }: {
    currentPage?: number
    pageSize?: number
  } = {}) => {
    try {
      startLoading()
      dispatch(
        getEndpoints({
          currentPage,
          pageSize,
        }),
      )
        .then((result: any) => {
          if (getEndpoints.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) || 'Error al crear la app',
            )
          } else if (getEndpoints.fulfilled.match(result)) {
            showAlert('success', 'Endpoints obtenidos correctamente')
            if (
              {
                currentPage,
                pageSize,
              }
            ) {
              handleTotalItems(result.payload.details)
            }
          }
        })
        .finally(() => {
          stopLoading()
        })
    } catch (error) {
      showAlert('error', 'Error al obtener los endpoints')
    }
  }

  /**
   * Deletes an endpoint by its ID and reloads the list of endpoints.
   *
   * @param {number} endpointId - The ID of the endpoint to delete.
   */
  const removeEndpoint = (endpointId: number) => {
    startLoading()
    dispatch(deleteEndpoint(endpointId))
      .then((result: any) => {
        if (deleteEndpoint.rejected.match(result)) {
          showAlert('error', String(result.payload) || 'Error al crear la app')
        } else if (deleteEndpoint.fulfilled.match(result)) {
          showAlert('success', 'Endpoint eliminado correctamente')
          loadEndpoints()
        }
      })
      .finally(() => {
        stopLoading()
      })
  }

  /**
   * Updates an existing endpoint with the provided payload.
   *
   * @param {IBodyEndpoint} payload - The data to update the endpoint.
   */
  const updateEndpointAdmin = (payload: IBodyEndpoint) => {
    startLoading()
    dispatch(updateEndpoint(payload))
      .then((result: any) => {
        if (updateEndpoint.rejected.match(result)) {
          showAlert('error', String(result.payload) || 'Error al crear la app')
        } else if (updateEndpoint.fulfilled.match(result)) {
          showAlert('success', 'Endpoint actualizado correctamente')
          loadEndpoints()
        }
      })
      .finally(() => {
        stopLoading()
      })
  }

  /**
   * Creates a new endpoint with the provided payload.
   *
   * @param {IEndpoint} payload - The data to create a new endpoint.
   */
  const createEndpointAdmin = (payload: IEndpoint) => {
    startLoading()
    dispatch(createEndpoint(payload))
      .then((result: any) => {
        if (createEndpoint.rejected.match(result)) {
          showAlert('error', String(result.payload) || 'Error al crear la app')
        } else if (createEndpoint.fulfilled.match(result)) {
          showAlert('success', 'Endpoint creado correctamente')
          loadEndpoints()
        }
      })
      .finally(() => {
        stopLoading()
      })
  }

  /**
   * Clears the current list of endpoints from the Redux state.
   */
  const clearEndpoints = () => {
    dispatch(resetEndpoint())
  }

  return {
    endpoints,
    endpoint,
    loading,
    error,
    updateEndpointAdmin,
    createEndpointAdmin,
    loadEndpoints,
    removeEndpoint,
    clearEndpoints,
  }
}
